/**
 * Small fetch wrapper with a default timeout.
 * Kiosk auth uses HttpOnly cookies + device GPS headers (no JWT in storage).
 */
import { getDeviceLocationHeaders } from '../../shared/deviceLocationCache';

const DEFAULT_TIMEOUT_MS = 20000;

function withKioskHeaders(initHeaders) {
  const headers = new Headers(initHeaders || {});
  const locationHeaders = getDeviceLocationHeaders();
  Object.entries(locationHeaders).forEach(([key, value]) => {
    if (!headers.has(key)) headers.set(key, value);
  });
  return headers;
}

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit & { timeoutMs?: number }} [init]
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(input, init = {}) {
  const { timeoutMs, signal, headers: initHeaders, ...rest } = init || {};
  const headers = withKioskHeaders(initHeaders);
  const ms = Number.isFinite(timeoutMs) ? timeoutMs : DEFAULT_TIMEOUT_MS;

  if (typeof AbortController === 'undefined') {
    return fetch(input, {
      credentials: rest.credentials || 'include',
      ...rest,
      headers,
      signal,
    });
  }

  const controller = new AbortController();
  let didTimeout = false;
  const timeoutId = setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, Math.max(0, ms));

  if (signal) {
    if (signal.aborted) controller.abort();
    else {
      const onAbort = () => controller.abort();
      signal.addEventListener('abort', onAbort, { once: true });
    }
  }

  try {
    return await fetch(input, {
      credentials: rest.credentials || 'include',
      ...rest,
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    if (didTimeout && (err?.name === 'AbortError' || err?.code === 20)) {
      const e = new Error(`Request timed out after ${ms}ms`);
      e.name = 'TimeoutError';
      e.cause = err;
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}
